import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import { getPlanById } from "../constants/plans";
import "../Css/Dashboard.css";

const Dashboard = () => {
  const { currentUser, logout } = useAuth();
  const { profile, profileLoading } = useSubscription();
  const navigate = useNavigate();
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [cancelSuccess, setCancelSuccess] = useState(false);

  if (profileLoading) return <div className="pageLoader">Loading your dashboard...</div>;

  const plan = profile?.plan || "free";
  const planDetails = getPlanById(plan);
  const status = profile?.subscription_status || "none";
  const periodEnd = profile?.current_period_end
    ? new Date(profile.current_period_end).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.")) return;

    setCancelError("");
    setCancelLoading(true);

    try {
      const response = await fetch("/api/cancel-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: currentUser.id }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to cancel subscription");
      setCancelSuccess(true);
    } catch (err) {
      setCancelError(err.message);
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <div className="dashboardPage">
      <div className="dashboardHeader">
        <h1>Welcome back{currentUser?.user_metadata?.full_name ? `, ${currentUser.user_metadata.full_name.split(" ")[0]}` : ""}!</h1>
        <p>{currentUser?.email}</p>
      </div>

      {cancelError && (
        <div style={{ maxWidth: 900, margin: "0 auto 1em", color: "#b91c1c", fontFamily: "Poppins, sans-serif" }}>
          {cancelError}
        </div>
      )}
      {cancelSuccess && (
        <div style={{ maxWidth: 900, margin: "0 auto 1em", color: "#065f46", background: "#d1fae5", padding: "0.8em 1em", borderRadius: "0.6em", fontFamily: "Poppins, sans-serif" }}>
          Subscription cancelled. You'll retain access until {periodEnd}.
        </div>
      )}

      <div className="dashboardGrid">
        <div className="dashboardCard">
          <h3>Current Plan</h3>
          <div className="cardValue">
            <span className={`planTag ${plan}`}>
              {plan === "free" ? "Free" : planDetails?.name || plan}
            </span>
          </div>
          {plan !== "free" && (
            <p className="cardSub">
              ₹{planDetails?.price.toLocaleString("en-IN")}/month
            </p>
          )}
        </div>

        <div className="dashboardCard">
          <h3>Subscription Status</h3>
          <div className="cardValue">
            <span className={`statusTag ${status}`}>
              {status === "none" ? "Not subscribed" : status.replace("_", " ")}
            </span>
          </div>
          {periodEnd && (
            <p className="cardSub">Renews on {periodEnd}</p>
          )}
        </div>

        <div className="dashboardCard">
          <h3>AI Agents</h3>
          <div className="cardValue">
            {plan === "free" ? "0" : plan === "starter" ? "1" : plan === "professional" ? "Up to 10" : "Unlimited"}
          </div>
          <p className="cardSub">Deployments included</p>
        </div>

        <div className="dashboardCard">
          <h3>Workflow Executions</h3>
          <div className="cardValue">
            {plan === "free" ? "0" : plan === "starter" ? "1,000" : plan === "professional" ? "50,000" : "Unlimited"}
          </div>
          <p className="cardSub">Per month</p>
        </div>
      </div>

      {planDetails && (
        <div className="dashboardFullCard">
          <h3>Your Plan Features</h3>
          <ul className="featureList">
            {planDetails.features.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="dashboardActions">
        {plan === "free" || !planDetails ? (
          <Link to="/pricing" className="dashBtn">
            <span>View Plans &amp; Upgrade</span>
          </Link>
        ) : (
          <>
            <Link to="/pricing" className="dashBtn">
              <span>Change Plan</span>
            </Link>
            {status === "active" && !cancelSuccess && (
              <button
                className="dashBtnDanger"
                onClick={handleCancel}
                disabled={cancelLoading}
              >
                {cancelLoading ? "Cancelling..." : "Cancel Subscription"}
              </button>
            )}
          </>
        )}

        <button className="dashBtnOutline" onClick={handleLogout}>
          Log out
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
